import { createGlobalStyle } from 'styled-components';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface GlobalStylesProps {
  theme: any;
  isAuthenticated?: boolean;
}

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/**
 * Converte um objeto JSON em variáveis CSS customizadas
 * @param json - Objeto com chave-valor para converter em CSS variables
 * @returns String com variáveis CSS formatadas
 */
const jsonToCSSVariables = (json: Record<string, string>): string =>
  Object.entries(json)
    .map(([key, value]) => `--${key}: ${value};`)
    .join('\n');

/**
 * Retorna o estilo de background baseado no estado de autenticação
 * @param isAuthenticated - Indica se o usuário está autenticado
 * @param isMobile - Indica se é uma tela mobile
 * @returns String com o CSS do background
 */
const getBackgroundStyle = (
  isAuthenticated: boolean,
  isMobile: boolean = false,
): string => {
  if (isAuthenticated) {
    return 'background: var(--color-background-container);';
  }

  return isMobile
    ? 'background: var(--color-background-mobile);'
    : 'background: var(--color-background);';
};

// ============================================================================
// ESTILOS GLOBAIS
// ============================================================================

export const GlobalStyles = createGlobalStyle<GlobalStylesProps>`
  /* ========================================================================
     VARIÁVEIS CSS ROOT
     ======================================================================== */
  :root {
    /* Converte tokens do tema em variáveis CSS */
    ${({ theme }) => jsonToCSSVariables(theme.token || {})}
    ${({ theme }) => jsonToCSSVariables(theme.designTokens || {})}
    
    /* Reset de box-sizing */
    box-sizing: border-box;
  }

  /* ========================================================================
     ELEMENTOS HTML BASE
     ======================================================================== */
  html {
    height: auto;
    min-height: 100%;
    margin: 0;
    padding: 0;
    font-family: var(--fontFamily);
  }

  body {
    margin: 0;
    padding: 0;
    font-family: var(--fontFamily);
    color: var(--color-neutral-light);
    
    /* Background condicional baseado em autenticação */
    ${({ isAuthenticated }) => getBackgroundStyle(Boolean(isAuthenticated))}
  }

  /* Background específico para mobile */
  @media (max-width: 768px) {
    body {
      ${({ isAuthenticated }) => getBackgroundStyle(Boolean(isAuthenticated), true)}
    }
  }

  /* ========================================================================
     CONTAINER PRINCIPAL
     ======================================================================== */
  #root {
    display: flex !important;
    flex-direction: column !important;
    height: 100%;
    min-height: 100vh;
  }

  /* ========================================================================
     SCROLLBAR CUSTOMIZADA (WebKit)
     ======================================================================== */
  ::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  ::-webkit-scrollbar-button {
    width: 4px;
    height: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--color-background);
    border: 0px none #ffffff;
    border-radius: 100px;
  }

  ::-webkit-scrollbar-track {
    background: var(--colorBgHeader);
    border: 0px none #ffffff;
  }

  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* ========================================================================
     COMPONENTES ESPECÍFICOS
     ======================================================================== */
  
  /* Drawer */
  .drawer-open {
    /* Adicione estilos específicos para drawer se necessário */
  }

  /* Modal do Vertice */
  .vert-modal.ant-modal-root .ant-modal-mask {
    background-color: rgba(51, 51, 51, 0.7) !important;
  }
  
  /* Footer de Welcome */
  .welcome-footer-container .welcome-footer-box {
    color: var(--footer-color-font);
  }
  
  .welcome-footer-container .welcome-footer-box a {
    color: var(--footer-color-button-text);
  }
  
  /* Header federado (ex.: HeaderDisconnected): linha inferior */
  .sh-header {
    border-bottom: 1px solid #d3d3d3;
  }

  /* Botão FML */
  .button-fml {
    box-shadow: var(--button-box-shadow-fml);
  }

  /* Tipografia */
  .font-medium, 
  .weight-bold {
    font-family: var(--font-family-bold);
    font-weight: 700;
  }
`;
